import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import HelpIcon from "../InvestmentScreening/HelpIcon";
import { formatLocalDateTime } from '../../utils/utils';
import i18n from '../../utils/i18n';

const T_PREFIX = 'features.MasterMaintenance.ActiveUsers';

type ActiveUser = {
  userId: string;
  lastAccessedAt: string;
};

/** UTC文字列 → ブラウザのローカル日時文字列（言語設定に応じたロケール表記） */
const toLocalDateTimeStr = (utcStr: string): string => {
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return utcStr;
  return formatLocalDateTime(utcStr);
};

/** 経過分数を計算 */
const elapsedMinutes = (utcStr: string): number => {
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 60000);
};

/**
 * 経過分数に応じたステータスドット色
 * 0〜20分 : アクティブ（青）
 * 21〜40分: 離席中（グレー）
 * 41〜60分: まもなく切断（薄グレー）
 */
/**
 * 0〜20分 : 濃い青（アクティブ）
 * 21〜40分: 明るい水色（離席中）
 * 41〜60分: 薄いグレー（まもなく切断）
 */
const statusDotColor = (mins: number): string => {
  if (mins <= 20) return 'blue.600';
  if (mins <= 40) return 'cyan.300';
  return 'gray.300';
};

const rowBgColor = (mins: number): string => {
  if (mins <= 40) return 'white';
  return 'gray.50';
};

const ActiveUsers: React.FC = () => {
  const WebAPI = useContext(WebAPIContext);
  const { t } = useTranslation();

  const [data, setData] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await WebAPI.request({
        query: `query {
          getActiveUsers {
            userId
            lastAccessedAt
          }
        }`,
      });
      setData(res?.data?.getActiveUsers ?? []);
      setLastFetchedAt(new Date());
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [WebAPI]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerCellStyle = {
    fontWeight: 'bold',
    fontSize: '14px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    backgroundColor: 'gray.100',
  } as const;

  const bodyCellStyle = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const toElapsedLabel = (mins: number): string => {
    if (mins < 1) return t(`${T_PREFIX}.label.lessThanOneMinute`);
    return t(`${T_PREFIX}.label.minutesElapsed`, { mins });
  };

  return (
    <Box h="100%" mt="16px">

      {/* ─── ツールバー ─── */}
      <Box px={2} pb={2} display="flex" alignItems="center" gap={2}>
        <Tooltip label={t(`${T_PREFIX}.tooltip.reload`)} placement="right">
          <IconButton
            aria-label={t(`${T_PREFIX}.label.reload`)}
            icon={<MdRefresh size={20} />}
            size="sm"
            variant="outline"
            isLoading={loading}
            onClick={fetchData}
          />
        </Tooltip>
        {lastFetchedAt && (
          <Text fontSize="sm" color="gray.500">
            {t(`${T_PREFIX}.label.fetchedAt`, { time: lastFetchedAt.toLocaleTimeString(i18n.language === 'en' ? 'en-US' : 'ja-JP') })}
          </Text>
        )}
        <HelpIcon size='25px' width="auto" placement="right">
          {t(`${T_PREFIX}.Help.explanation`)}
        </HelpIcon>
      </Box>

      {/* ─── ボディ ─── */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" pt={16}>
          <Spinner size="lg" />
        </Box>
      ) : data.length === 0 ? (
        <Box px={6}>
          <Alert
            status="success"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            py={10}
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} mb={3} />
            <AlertTitle fontSize="xl">{t(`${T_PREFIX}.empty.title`)}</AlertTitle>
            <Text mt={2} color="gray.600" fontSize="sm">
              {t(`${T_PREFIX}.empty.description`)}
            </Text>
          </Alert>
        </Box>
      ) : (
        <TableContainer
          overflowX="auto"
          overflowY="auto"
          h="calc(100dvh - 120px)"
        >
          <Table
            variant="simple"
            style={{ tableLayout: 'fixed', borderCollapse: 'separate' }}
            cellSpacing={0}
            cellPadding={0}
            className="mm-ceas-table"
            w="900px"
          >
            <colgroup>
              <col width={400} />
              <col width={220} />
              <col width={160} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow="0 -1px 0 #EEEEEE">
              <Tr h={50}>
                <Th {...headerCellStyle}>{t(`${T_PREFIX}.column.userId`)}</Th>
                <Th {...headerCellStyle}>{t(`${T_PREFIX}.column.lastAccessedAt`)}</Th>
                <Th {...headerCellStyle} textAlign="center">{t(`${T_PREFIX}.column.elapsed`)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((row, i) => {
                const mins = elapsedMinutes(row.lastAccessedAt);
                return (
                  <Tr
                    key={i}
                    bg={rowBgColor(mins)}
                    _hover={{ bg: 'gray.100' }}
                  >
                    <Td {...bodyCellStyle}>{row.userId}</Td>
                    <Td {...bodyCellStyle}>{toLocalDateTimeStr(row.lastAccessedAt)}</Td>
                    <Td {...bodyCellStyle} textAlign="center">
                      <Box display="inline-flex" alignItems="center" gap={2}>
                        <Box
                          as="span"
                          w="14px"
                          h="14px"
                          borderRadius="full"
                          bg={statusDotColor(mins)}
                          flexShrink={0}
                        />
                        <Text as="span" color="gray.600" fontSize="md" fontWeight="medium">
                          {toElapsedLabel(mins)}
                        </Text>
                      </Box>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ActiveUsers;
